"use client";

import { useState } from "react";
import { RxCross2 } from "react-icons/rx";
import Image from "next/image";
import InputField from "../ui/InputField";
import Button from "../ui/buttons";
interface AdvertiseFormProps {
  onClose: () => void;
}
const SECTION_META_API_BASE = process.env.NEXT_PUBLIC_SECTION_META_API_BASE_URL || 'https://builderapi.dinamani.com';
export default function AdvertiseForm({
  onClose
}: AdvertiseFormProps) {
  const [formData, setFormData] = useState({
    name: "",
    companyName: "",
    contactNumber: "",
    email: "",
    remarks: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<{
    type: "success" | "error";
    message: string;
  } | null>(null);
  const validateEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };
  const validatePhone = (phone: string) => {
    // Allow various phone formats: 10 digits, with country code, with dashes/spaces
    const cleanedPhone = phone.replace(/[\s\-\(\)]/g, "");
    return cleanedPhone.length >= 10 && /^[\+]?[0-9]+$/.test(cleanedPhone);
  };
  const handleSubmit = async () => {
    setSubmitStatus(null);

    // Validation - Required fields
    if (!formData.name.trim()) {
      setSubmitStatus({
        type: "error",
        message: "Please enter your name"
      });
      return;
    }
    if (!formData.companyName.trim()) {
      setSubmitStatus({
        type: "error",
        message: "Please enter your company name"
      });
      return;
    }
    if (!formData.contactNumber.trim()) {
      setSubmitStatus({
        type: "error",
        message: "Please enter your contact number"
      });
      return;
    }
    if (!validatePhone(formData.contactNumber)) {
      setSubmitStatus({
        type: "error",
        message: "Please enter a valid contact number"
      });
      return;
    }
    if (!formData.email.trim()) {
      setSubmitStatus({
        type: "error",
        message: "Please enter your email"
      });
      return;
    }
    if (!validateEmail(formData.email)) {
      setSubmitStatus({
        type: "error",
        message: "Please enter a valid email"
      });
      return;
    }
    setIsSubmitting(true);
    try {
      // Submit to API
      const response = await fetch(`${SECTION_META_API_BASE}/api/contact/send`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          name: formData.name.trim(),
          companyName: formData.companyName.trim(),
          contactNumber: formData.contactNumber.trim(),
          email: formData.email.trim(),
          remarks: formData.remarks.trim() || undefined
        })
      });
      if (response.ok) {
        setSubmitStatus({
          type: "success",
          message: "Thank you! We will contact you soon."
        });
        // Reset form
        setFormData({
          name: "",
          companyName: "",
          contactNumber: "",
          email: "",
          remarks: ""
        });
        // Close after 2 seconds
        setTimeout(() => onClose(), 2000);
      } else {
        const errorData = await response.json().catch(() => null);
        setSubmitStatus({
          type: "error",
          message: errorData?.message || "Failed to submit. Please try again."
        });
      }
    } catch {
      setSubmitStatus({
        type: "error",
        message: "Failed to submit. Please try again."
      });
    } finally {
      setIsSubmitting(false);
    }
  };
  return <div className="bg-F8F8F8 w-full h-full overflow-x-hidden p-[10px] sm:p-6">
      <div className="flex flex-col gap-4">
        {/* Header Section */}
        <div className="relative">
          {/* Close Button */}
          <button onClick={onClose} className="absolute right-0 top-0 text-808080-600 hover:text-000000 transition-all duration-200 transform hover:scale-110 cursor-pointer" aria-label="Close">
            <RxCross2 size={22} />
          </button>
        </div>

        {/* Logo */}
        <div className="flex justify-center">
          <Image src="https://media.dinamani.com/dinamani/2026-02-27/y9s7ztw3/dinamanilogo.png" alt="Dinamani Logo" width={100} height={43} className="object-contain" />
        </div>

        {/* Title */}
        <h2 className="text-center text-18-tamil-700 text-1B1B1B">
          To advertise on
        </h2>
        <p className="text-center text-18-tamil-700 text-1B1B1B -mt-2">
          Dinamani.com
        </p>

        {/* Subtitle */}
        <p className="text-center text-4D4D4D text-14-tamil-400">
          Please fill out the form below
        </p>

        {/* Divider */}
        <div className="-mx-[10px] sm:-mx-6 border-b border-DCDCDC"></div>

        {/* Status Message */}
        {submitStatus && <div className={`p-3 text-14-tamil-400 text-center rounded ${submitStatus.type === "success" ? "bg-green-50 border border-green-200 text-green-600" : "bg-red-50 border border-red-200 text-red-600"}`}>
            {submitStatus.message}
          </div>}

        {/* Form Fields */}
        <div className="flex flex-col gap-3">
          <InputField placeholder="Name *" value={formData.name} onChange={val => setFormData(prev => ({
          ...prev,
          name: val
        }))} height="44px" />

          <InputField placeholder="Company Name *" value={formData.companyName} onChange={val_0 => setFormData(prev_0 => ({
          ...prev_0,
          companyName: val_0
        }))} height="44px" />

          <InputField placeholder="Contact Number *" value={formData.contactNumber} onChange={val_1 => setFormData(prev_1 => ({
          ...prev_1,
          contactNumber: val_1
        }))} height="44px" />

          <InputField placeholder="Email *" value={formData.email} onChange={val_2 => setFormData(prev_2 => ({
          ...prev_2,
          email: val_2
        }))} height="44px" />

          <textarea placeholder="Remarks (Optional)" value={formData.remarks} onChange={e => setFormData(prev_3 => ({
          ...prev_3,
          remarks: e.target.value
        }))} className="w-full p-3 border border-D2D2D2 bg-FFFFFF text-14-tamil-400 text-1B1B1B placeholder:text-808080 focus:outline-none focus:border-1B1B1B resize-none" rows={3} />

          {/* Submit Button */}
          <Button variant="primary" onClick={handleSubmit} disabled={isSubmitting} className="w-full text-18-tamil-700 text-FFFFFF cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed mt-2">
            {isSubmitting ? "Submitting..." : "Submit"}
          </Button>

          {/* Contact Info */}
          <p className="text-center text-4D4D4D text-12-tamil-400 mt-3">
            You can also reach us at{" "}
            <a href="mailto:onlinesales@newindianexpress.com" className="text-blue-600 hover:underline">
              onlinesales@newindianexpress.com
            </a>
            .<br />
            Our team will respond promptly.
          </p>
        </div>

        {/* Footer */}
        <div className="text-center pt-2 text-4D4D4D text-10-tamil-400">
          <p className="leading-5">
            By submitting, you agree to our Terms of Use and Privacy Policy.
          </p>
        </div>
      </div>
    </div>;
}